"use client";
import React from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

interface FilterItem {
  key: string;
  label?: string;
}

interface AppliedFiltersProps {
  startDate?: string;
  endDate?: string;
  district?: string;
  districtLabel?: string;
  municipality?: string;
  municipalityLabel?: string;
  publicationType?: string;
  contentTheme?: string;
  contentThemeLabel?: string;
  publicationTypeLabel?: string;
  region?: string;
  category?: string;
  categoryLabel?: string;
  regionLabel?: string;
  searchTerm?: string;
  onRemoveFilter?: (filterType: string) => void;
  onClearAll?: () => void;
}

export default function AppliedFilters(props: AppliedFiltersProps) {
  const t = useTranslations("Filters");

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const formatDateForDisplay = (dateStr?: string): string | undefined => {
    if (!dateStr) return undefined;

    try {
      const dateObj = new Date(dateStr);
      if (!isNaN(dateObj.getTime())) {
        const day = String(dateObj.getUTCDate()).padStart(2, "0");
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
        const year = dateObj.getUTCFullYear();
        return `${day}/${month}/${year}`;
      }

      return dateStr;
    } catch (error) {
      console.error("Erro ao formatar data para exibição:", error);
      return undefined;
    }
  };

  const handleRemoveFilter = (filterType: string) => {
    if (props.onRemoveFilter) {
      props.onRemoveFilter(filterType);
      return;
    }

    const params = new URLSearchParams();

    searchParams?.forEach((value, key) => {
      if (key !== filterType && key !== "page") {
        params.set(key, value);
      }
    });

    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const handleClearAll = () => {
    if (props.onClearAll) {
      props.onClearAll();
      return;
    }

    const params = new URLSearchParams();

    searchParams?.forEach((value, key) => {
      if (
        key !== "startDate" &&
        key !== "endDate" &&
        key !== "district" &&
        key !== "municipality" &&
        key !== "publicationType" &&
        key !== "contentTheme" &&
        key !== "region" &&
        key !== "search" &&
        key !== "category" &&
        key !== "page"
      ) {
        params.set(key, value);
      }
    });

    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const appliedFilters: FilterItem[] = [];

  if (props.searchTerm) {
    appliedFilters.push({
      key: "search",
      label: `"${props.searchTerm}"`,
    });
  }

  if (props.publicationType && props.publicationTypeLabel) {
    appliedFilters.push({
      key: "publicationType",
      label: props.publicationTypeLabel,
    });
  }

  if (props.category && props.categoryLabel) {
    appliedFilters.push({
      key: "category",
      label: props.categoryLabel,
    });
  }

  if (props.contentTheme && props.contentThemeLabel) {
    appliedFilters.push({
      key: "contentTheme",
      label: props.contentThemeLabel,
    });
  }

  if (props.region && props.regionLabel) {
    appliedFilters.push({ key: "region", label: props.regionLabel });
  }

  if (props.district && props.districtLabel) {
    appliedFilters.push({ key: "district", label: props.districtLabel });
  }

  if (props.municipality && props.municipalityLabel) {
    appliedFilters.push({
      key: "municipality",
      label: props.municipalityLabel,
    });
  }

  const startDateDisplay = formatDateForDisplay(props.startDate);
  if (startDateDisplay) {
    appliedFilters.push({ key: "startDate", label: startDateDisplay });
  }

  const endDateDisplay = formatDateForDisplay(props.endDate);
  if (endDateDisplay) {
    appliedFilters.push({ key: "endDate", label: endDateDisplay });
  }

  const filtersCount = appliedFilters.length;

  return (
    <div>
      {filtersCount > 0 && (
        <div className="my-24">
          <h3 className="text-m-bold">
            {t("filter")} ({filtersCount})
          </h3>

          <div className="inline-flex gap-8 flex-col py-32">
            {appliedFilters.map((filter) => (
              <div
                key={filter.key}
                className={`w-auto inline-flex items-center gap-8 border-2 rounded-4 p-16 border-neutral-700`}
              >
                <span className={`capitalize text-s-medium'text-neutral-900`}>
                  {filter.label}
                </span>
                <button
                  onClick={() => handleRemoveFilter(filter.key)}
                  className={`font-bold text-neutral-900`}
                  aria-label={`${t("remove")} ${filter.label}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={handleClearAll}
            className="flex gap-8 items-center gap-4 text-primary-600"
          >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7 5V4C7 3.20435 7.31607 2.44129 7.87868 1.87868C8.44129 1.31607 9.20435 1 10 1H14C14.7956 1 15.5587 1.31607 16.1213 1.87868C16.6839 2.44129 17 3.20435 17 4V5H21C21.5523 5 22 5.44772 22 6C22 6.55228 21.5523 7 21 7H20V20C20 20.7957 19.6839 21.5587 19.1213 22.1213C18.5587 22.6839 17.7957 23 17 23H7C6.20435 23 5.44129 22.6839 4.87868 22.1213C4.31607 21.5587 4 20.7957 4 20V7H3C2.44772 7 2 6.55228 2 6C2 5.44772 2.44772 5 3 5H7ZM9.29289 3.29289C9.48043 3.10536 9.73478 3 10 3H14C14.2652 3 14.5196 3.10536 14.7071 3.29289C14.8946 3.48043 15 3.73478 15 4V5H9V4C9 3.73478 9.10536 3.48043 9.29289 3.29289ZM6 7H18V20C18 20.2652 17.8946 20.5196 17.7071 20.7071C17.5196 20.8946 17.2652 21 17 21H7C6.73478 21 6.48043 20.8946 6.29289 20.7071C6.10536 20.5196 6 20.2652 6 20V7Z"
                  fill="#0E8668"
                />
              </svg>
            <span className="text-m-semibold">{t("clearfilter")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
